import { CSSResultGroup, html, nothing } from 'lit'
import { customElement, property, state, query } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { style } from './upload.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { ValueFormElement } from 'elements/_shared/formElement.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { BREAKPOINTS, ConcreteBreakpoints } from '@cloudsuite/mosaic-shared/scripts/breakpoints.js'
import { breakpoints } from '@cloudsuite/mosaic-shared/styles/breakpoints.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'


initializeIconsByRole({
  'cse-upload.icon': { name: 'cloud-download', style: 'regular' }
})

export type FileValidationResult = {
  isValid: boolean;
  error?: string;
};

export interface FileUploadConfig {
  maxSizeInBytes?: number;
  acceptedTypes?: string[];
  dropText?: string;
  clickText?: string;
}

export class CSEUploadValidatedEvent extends Event {
  static type = 'file-validated' as const

  public file: File

  constructor(file: File) {
    super(CSEUploadValidatedEvent.type, { bubbles: true, composed: true })
    this.file = file
  }
}

export class CSEUploadValidationErrorEvent extends Event {
  static type = 'validation-error' as const

  public file: File
  public error: string

  constructor(file: File, error: string) {
    super(CSEUploadValidationErrorEvent.type, { bubbles: true, composed: true })
    this.file = file
    this.error = error
  }
}

@customElement('cse-upload')
export class CSEUpload extends ValueFormElement {
  static override styles: CSSResultGroup = [hostStyle, style, fontStyles]

  @property({ type: String, attribute: 'id' })
  _id = ''

  @property({ type: String })
  override name = ''

  @property({ type: Boolean })
  required = false

  @property({ type: Boolean })
  disabled = false

  @property({ type: Object })
  config: FileUploadConfig = {
    acceptedTypes: ['text/csv'],
    maxSizeInBytes: 5 * 1024 * 1024, // 5MB default
    dropText: 'Drop file here or',
    clickText: 'choose file'
  }

  @property({ type: Boolean, reflect: true })
  dragging = false

  @property({ type: Boolean, reflect: true, attribute: 'mobile-screen' })
  isMobileScreen = false

  @state()
  private isDragging = false

  @state()
  private error: string | null = null

  @state()
  private isValidating = false

  @inject(BREAKPOINTS)
  private breakpoints!: ConcreteBreakpoints

  @query('input[type="file"]')
  private fileInput!: HTMLInputElement

  private _file: File | null = null

  constructor() {
    super()
    this.breakpoints.matchMedia(breakpoints.medium)
    createEffect(() => {
      this.isMobileScreen = this.breakpoints.small()
    })
  }

  override get value(): string | File {
    return this._file || ''
  }

  override set value(val: string | File) {
    const oldValue = this._file
    if (val instanceof File) {
      this._file = val
    } else {
      this._file = null
    }
    this.requestUpdate('value', oldValue)
  }

  private validateFile(file: File): FileValidationResult {
    // Type validation
    const acceptedTypes = this.config.acceptedTypes || ['*/*']
    if (acceptedTypes[0] !== '*/*' && !acceptedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: `Please upload a file of type: ${acceptedTypes.join(', ')}`
      }
    }

    // Size validation
    if (this.config.maxSizeInBytes && file.size > this.config.maxSizeInBytes) {
      return {
        isValid: false,
        error: `File size must be less than ${Math.round(this.config.maxSizeInBytes / 1024 / 1024)}MB`
      }
    }

    // Required validation
    if (this.required && !file) {
      return {
        isValid: false,
        error: 'A file is required'
      }
    }

    return { isValid: true }
  }

  private async handleFiles(files: FileList) {
    const file = files[0]
    if (!file) return

    this.isValidating = true
    this.error = null

    try {
      const validationResult = this.validateFile(file)

      if (!validationResult.isValid && validationResult.error) {
        this.error = validationResult.error
        this.dispatchEvent(new CSEUploadValidationErrorEvent(file, validationResult.error))
        return
      }

      this.value = file
      this.dispatchEvent(new CSEUploadValidatedEvent(file))
    } catch {
      const errorMessage = 'An error occurred while validating the file'
      this.error = errorMessage
      this.dispatchEvent(new CSEUploadValidationErrorEvent(file, errorMessage))
    } finally {
      this.isValidating = false
    }
  }

  private handleDragEnter(e: DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    if (!this.disabled) {
      this.isDragging = true
      this.dragging = true
      this.requestUpdate()
    }
  }

  private handleDragLeave(e: DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    this.isDragging = false
    this.dragging = false
    this.requestUpdate()
  }

  private handleDragOver(e: DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    if (!this.disabled) {
      this.isDragging = true
      this.dragging = true
    }
  }

  private handleDrop(e: DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    this.isDragging = false
    this.dragging = false
    this.requestUpdate()

    if (!this.disabled && e.dataTransfer?.files) {
      this.handleFiles(e.dataTransfer.files)
    }
  }

  private handleFileInput(e: Event) {
    const input = e.target as HTMLInputElement
    if (input.files) {
      this.handleFiles(input.files)
    }
  }

  private handleChooseFileClick = (e: Event) => {
    e.preventDefault()
    e.stopPropagation()
    if (!this.disabled) {
      this.fileInput.click()
    }
  }

  private renderStatus = () => html`
    ${this.isValidating ? html`
      <div class="status font-caption-m">
        Validating file...
      </div>
    ` : nothing}
  `

  private renderError = () => html`
    ${this.error ? html`
      <div class="error font-caption-m">
        ${this.error}
      </div>
    ` : nothing}
  `

  override render() {
    const classes = classMap({
      'cse-upload': true,
      'dragging': this.isDragging || this.dragging,
      'disabled': this.disabled,
      'validating': this.isValidating,
      'has-error': !!this.error
    })

    return html`
      <div class="${classes}"
        @dragenter=${this.handleDragEnter}
        @dragleave=${this.handleDragLeave}
        @dragover=${this.handleDragOver}
        @drop=${this.handleDrop}
      >
        <cse-icon
          class="icon"
          icon-role="cse-upload.icon"
          size="lg"
        ></cse-icon>
        <div class="text font-body">
          ${!this.isMobileScreen ? this.config.dropText : nothing}
          <input
            type="file"
            id="${this._id}"
            name="${this.name}"
            ?required="${this.required}"
            ?disabled="${this.disabled}"
            accept="${this.config.acceptedTypes?.join(',')}"
            @change=${this.handleFileInput}
            style="display: none;"
          />
          <cse-link @click=${this.handleChooseFileClick}>${this.config.clickText}</cse-link>
        </div>
        ${this.renderStatus()}
        ${this.renderError()}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-upload': CSEUpload;
  }
}
